import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Custom Pagination component
import Pagination from "components/common/pagination/Pagination";

// Services - AHORA SÍ CON INTEGRACIÓN REAL
import distributorService from "../../../../services/distributorService";

const ITEMS_PER_PAGE = 5;

// ========================================
// COMPONENTES AUXILIARES
// ========================================

const TextCell = ({ value, align = "left" }) => (
  <MDTypography variant="caption" fontWeight="medium" textAlign={align}>
    {value || "-"}
  </MDTypography>
);

TextCell.propTypes = {
  value: PropTypes.any,
  align: PropTypes.string,
};

const StatusBadge = ({ assigned, used, available }) => (
  <MDBox display="flex" flexDirection="column" gap={0.5}>
    <MDBox display="flex" alignItems="center" gap={1}>
      <MDTypography variant="caption" color="info" fontWeight="bold">
        Asignadas:
      </MDTypography>
      <MDTypography variant="caption" fontWeight="medium">
        {assigned}
      </MDTypography>
    </MDBox>
    <MDBox display="flex" alignItems="center" gap={1}>
      <MDTypography variant="caption" color="success" fontWeight="bold">
        Usadas:
      </MDTypography>
      <MDTypography variant="caption" fontWeight="medium">
        {used}
      </MDTypography>
    </MDBox>
    <MDBox display="flex" alignItems="center" gap={1}>
      <MDTypography variant="caption" color="warning" fontWeight="bold">
        Disponibles:
      </MDTypography>
      <MDTypography variant="caption" fontWeight="medium">
        {available}
      </MDTypography>
    </MDBox>
  </MDBox>
);

StatusBadge.propTypes = {
  assigned: PropTypes.number.isRequired,
  used: PropTypes.number.isRequired,
  available: PropTypes.number.isRequired,
};

const SelectCell = ({ empresaId, isSelected, onSelect, isDisabled }) => (
  <MDBox display="flex" justifyContent="center" alignItems="center">
    <Checkbox
      checked={isSelected}
      onChange={() => onSelect(empresaId)}
      disabled={isDisabled}
      sx={{
        color: "#9C27B0",
        "&.Mui-checked": {
          color: "#9C27B0",
        },
      }}
    />
  </MDBox>
);

SelectCell.propTypes = {
  empresaId: PropTypes.string.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
};

// ========================================
// COMPONENTE PRINCIPAL
// ========================================

function AsignarLicenciasEmpresa() {
  // Estados principales
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [cantidadLicencias, setCantidadLicencias] = useState("");

  // Estado de licencias del distribuidor
  const [licenciasDistribuidor, setLicenciasDistribuidor] = useState({
    total: 0,
    asignadas: 0,
    disponibles: 0,
    limiteMaximo: "Sin límites",
  });

  // Estado de empresas
  const [allEmpresas, setAllEmpresas] = useState([]);
  const [selectedEmpresas, setSelectedEmpresas] = useState({});
  const [licenciasToAssign, setLicenciasToAssign] = useState({});

  // Notificaciones
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    color: "success",
  });

  // ========================================
  // EFECTOS
  // ========================================

  useEffect(() => {
    loadEmpresas();
    loadLicenciasDistribuidor();
  }, []);

  // ========================================
  // FUNCIONES DE CARGA DE DATOS
  // ========================================

  const loadEmpresas = async () => {
    try {
      setLoading(true);
      console.log("📥 Cargando empresas del distribuidor...");
      
      const empresas = await distributorService.getEmpresasDistribuidor();
      
      console.log("✅ Empresas cargadas:", empresas.length);
      setAllEmpresas(empresas);
      
    } catch (error) {
      console.error("❌ Error al cargar empresas:", error);
      showNotification(error.message || "Error al cargar empresas", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadLicenciasDistribuidor = async () => {
    try {
      console.log("📊 Cargando licencias del distribuidor...");
      
      const licencias = await distributorService.getLicenciasDistribuidor();
      
      console.log("✅ Licencias cargadas:", licencias);
      setLicenciasDistribuidor(licencias);
      
    } catch (error) {
      console.error("❌ Error al cargar licencias:", error);
      showNotification("Error al cargar información de licencias", "error");
    }
  };

  // ========================================
  // FUNCIONES DE NOTIFICACIÓN
  // ========================================

  const showNotification = (message, color = "success") => {
    setNotification({
      open: true,
      message,
      color,
    });
  };

  const closeNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  // ========================================
  // MANEJO DE CONTROL DE CANTIDAD
  // ========================================

  const handleIncrementCantidad = () => {
    setCantidadLicencias((prev) => prev + 1);
  };

  const handleDecrementCantidad = () => {
    setCantidadLicencias((prev) => (prev > 0 ? prev - 1 : 0));
  };

  const handleCantidadChange = (value) => {
    const numValue = parseInt(value) || 0;
    // Solo permitir valores >= 0 (no negativos)
    if (numValue >= 0) {
      setCantidadLicencias(numValue);
    }
  };

  // ========================================
  // MANEJO DE SELECCIÓN Y ASIGNACIÓN
  // ========================================

  const handleSelectEmpresa = (empresaId) => {
    setSelectedEmpresas((prev) => ({
      ...prev,
      [empresaId]: !prev[empresaId],
    }));

    // Si se deselecciona, limpiar el input de licencias
    if (selectedEmpresas[empresaId]) {
      setLicenciasToAssign((prev) => {
        const newState = { ...prev };
        delete newState[empresaId];
        return newState;
      });
    }
  };

  const handleLicenciasChange = (empresaId, value) => {
    const numValue = parseInt(value) || 0;

    // Validar que no exceda las licencias disponibles
    if (numValue > licenciasDistribuidor.disponibles) {
      showNotification(
        `No puedes asignar más de ${licenciasDistribuidor.disponibles} licencias disponibles`,
        "warning"
      );
      return;
    }

    setLicenciasToAssign((prev) => ({
      ...prev,
      [empresaId]: numValue,
    }));
  };

  const handleAsignarLicencias = async () => {
    try {
      // Validar que haya empresas seleccionadas
      const selectedIds = Object.keys(selectedEmpresas).filter(
        (id) => selectedEmpresas[id]
      );

      if (selectedIds.length === 0) {
        showNotification("Por favor selecciona al menos una empresa", "warning");
        return;
      }

      // Validar cantidad de licencias
      if (cantidadLicencias <= 0) {
        showNotification("La cantidad de licencias debe ser mayor a 0", "warning");
        return;
      }

      // Calcular total de licencias a asignar
      const totalToAssign = selectedIds.length * cantidadLicencias;

      if (totalToAssign > licenciasDistribuidor.disponibles) {
        showNotification(
          `El total de licencias a asignar (${totalToAssign}) excede las licencias disponibles (${licenciasDistribuidor.disponibles})`,
          "error"
        );
        return;
      }

      setSubmitting(true);

      // Preparar datos para el servicio
      const asignaciones = selectedIds.map((id) => ({
        id_empresa: id,
        cantidad_licencias: cantidadLicencias,
      }));

      console.log("🚀 Asignando licencias:", asignaciones);

      // Llamar al servicio
      const resultado = await distributorService.asignarLicenciasEmpresas(asignaciones);

      if (resultado.success) {
        showNotification(
          `${totalToAssign} licencia(s) asignadas exitosamente a ${selectedIds.length} empresa(s)`,
          "success"
        );

        // Limpiar selección
        setSelectedEmpresas({});
        setLicenciasToAssign({});
        setCantidadLicencias(0);

        // Recargar datos
        await loadEmpresas();
        await loadLicenciasDistribuidor();
      } else {
        showNotification(
          resultado.message || "Error al asignar licencias",
          "error"
        );
      }
    } catch (error) {
      console.error("❌ Error al asignar licencias:", error);
      showNotification(error.message || "Error al asignar licencias", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // ========================================
  // FILTRADO Y PAGINACIÓN
  // ========================================

  const filteredEmpresas = useMemo(() => {
    if (!searchTerm) {
      return allEmpresas;
    }

    const lowercased = searchTerm.toLowerCase();
    return allEmpresas.filter(
      (empresa) =>
        empresa.nombre_empresa?.toLowerCase().includes(lowercased) ||
        empresa.nit?.toString().includes(lowercased)
    );
  }, [allEmpresas, searchTerm]);

  // Reset page cuando cambia el filtro
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Paginación
  const { paginatedEmpresas, totalItems } = useMemo(() => {
    const total = filteredEmpresas.length;
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    const paginated = filteredEmpresas.slice(start, end);

    return { paginatedEmpresas: paginated, totalItems: total };
  }, [filteredEmpresas, currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // ========================================
  // CONFIGURACIÓN DE TABLA
  // ========================================

  const tableColumns = [
    { Header: "", accessor: "select", width: "3%" },
    { Header: "Nombre de la empresa", accessor: "nombre", width: "12%" },
    { Header: "NIT", accessor: "nit", width: "8%" },
    { Header: "Correo", accessor: "correo", width: "12%" },
    { Header: "Teléfono", accessor: "telefono", width: "8%" },
    { Header: "Dirección", accessor: "direccion", width: "10%" },
    { Header: "Ciudad", accessor: "ciudad", width: "8%" },
    { Header: "Persona de contacto", accessor: "persona_contacto", width: "10%" },
    { Header: "Cargo", accessor: "cargo", width: "7%" },
    { Header: "Licencias", accessor: "licencias", width: "10%" },
    { Header: "Cantidad", accessor: "cantidad", width: "7%" },
    { Header: "Descripción", accessor: "descripcion", width: "10%" },
  ];

  const formattedRows = useMemo(() => {
    return paginatedEmpresas.map((empresa) => ({
      select: (
        <SelectCell
          empresaId={empresa.id_empresa}
          isSelected={!!selectedEmpresas[empresa.id_empresa]}
          onSelect={handleSelectEmpresa}
          isDisabled={loading}
        />
      ),
      nombre: <TextCell value={empresa.nombre_empresa} />,
      nit: <TextCell value={empresa.nit} />,
      correo: <TextCell value={empresa.email} />,
      telefono: <TextCell value={empresa.telefono} />,
      direccion: <TextCell value={empresa.direccion} />,
      ciudad: <TextCell value={empresa.ciudad || empresa.ciudad_operacion} />,
      persona_contacto: <TextCell value={empresa.persona_contacto} />,
      cargo: <TextCell value={empresa.cargo} />,
      licencias: (
        <StatusBadge
          assigned={empresa.licencias_compradas || 0}
          used={empresa.licencias_usadas || 0}
          available={(empresa.licencias_compradas || 0) - (empresa.licencias_usadas || 0)}
        />
      ),
      cantidad: (
        <MDBox display="flex" justifyContent="center">
          <MDInput
            type="number"
            value={licenciasToAssign[empresa.id_empresa] || ""}
            onChange={(e) => handleLicenciasChange(empresa.id_empresa, e.target.value)}
            disabled={!selectedEmpresas[empresa.id_empresa] || loading}
            inputProps={{
              min: 0,
              max: licenciasDistribuidor.disponibles,
            }}
            sx={{ width: "80px" }}
          />
        </MDBox>
      ),
      descripcion: <TextCell value={empresa.descripcion} />,
    }));
  }, [paginatedEmpresas, selectedEmpresas, licenciasToAssign, loading, licenciasDistribuidor.disponibles]);

  const tableData = useMemo(
    () => ({
      columns: tableColumns,
      rows: formattedRows,
    }),
    [formattedRows]
  );

  // ========================================
  // RENDER
  // ========================================

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          {/* Header Card - Información del Distribuidor */}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Asignar licencias - Empresa
                </MDTypography>
              </MDBox>

              <MDBox p={3}>
                <Grid container spacing={3}>
                  {/* Buscador de empresa */}
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="body2" fontWeight="bold" mb={1}>
                      Nit empresa cliente:
                    </MDTypography>
                    <MDInput
                      fullWidth
                      placeholder="Ingrese nombre de la empresa o Nit"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <Icon sx={{ mr: 1, color: "text.secondary" }}>search</Icon>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Control de Cantidad de Licencias */}
                  <Grid item xs={12} md={6}>
                    <MDBox
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-end"
                      height="100%"
                      justifyContent="center"
                    >
                      <MDTypography variant="body2" fontWeight="bold" mb={1}>
                        Cantidad de licencias:
                      </MDTypography>
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <IconButton
                          size="small"
                          onClick={handleDecrementCantidad}
                          disabled={loading || submitting || cantidadLicencias <= 0}
                          sx={{
                            color: "error.main",
                            "&:hover": { backgroundColor: "error.light", opacity: 0.1 },
                          }}
                        >
                          <Icon>remove_circle</Icon>
                        </IconButton>
                        <MDInput
                          type="number"
                          value={cantidadLicencias}
                          onChange={(e) => handleCantidadChange(e.target.value)}
                          disabled={loading || submitting}
                          inputProps={{
                            min: 0,
                            style: { textAlign: "center" },
                          }}
                          sx={{ width: "100px" }}
                        />
                        <IconButton
                          size="small"
                          onClick={handleIncrementCantidad}
                          disabled={loading || submitting}
                          sx={{
                            color: "success.main",
                            "&:hover": { backgroundColor: "success.light", opacity: 0.1 },
                          }}
                        >
                          <Icon>add_circle</Icon>
                        </IconButton>
                      </MDBox>
                      <MDTypography variant="caption" color="text" mt={1}>
                        Límite máximo: Sin límites
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>

          {/* Tabla de Empresas */}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Empresas Cliente ({totalItems} total
                  {filteredEmpresas.length !== allEmpresas.length
                    ? `, ${filteredEmpresas.length} filtradas`
                    : ""}
                  )
                </MDTypography>
              </MDBox>

              <MDBox pt={3}>
                {loading ? (
                  <MDBox p={3} textAlign="center">
                    <MDTypography variant="body2" color="text">
                      Cargando empresas...
                    </MDTypography>
                  </MDBox>
                ) : filteredEmpresas.length === 0 ? (
                  <MDBox p={3} textAlign="center">
                    <MDTypography variant="body2" color="text">
                      No se encontraron empresas
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={tableData}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                    canSearch={false}
                  />
                )}
              </MDBox>

              <MDBox pt={3} pb={2}>
                <Pagination
                  itemsPerPage={ITEMS_PER_PAGE}
                  totalItems={totalItems}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </MDBox>

              {/* Control de Cantidad y Botón de Asignar */}
              <MDBox px={3} pb={3}>
                <MDBox display="flex" justifyContent="flex-end" alignItems="center">
                  {/* Botón Asignar Licencias */}
                  <MDButton
                    variant="contained"
                    color="success"
                    onClick={handleAsignarLicencias}
                    disabled={
                      loading ||
                      submitting ||
                      cantidadLicencias <= 0 ||
                      Object.keys(selectedEmpresas).filter((id) => selectedEmpresas[id])
                        .length === 0
                    }
                  >
                    <Icon>assignment</Icon>&nbsp;
                    {submitting ? "Asignando..." : "Asignar Licencias"}
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />

      {/* Notificaciones */}
      <MDSnackbar
        color={notification.color}
        icon="notifications"
        title="Notificación"
        content={notification.message || ""}
        dateTime={new Date().toLocaleTimeString()}
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default AsignarLicenciasEmpresa;